import { debounce } from 'lodash';
import React from 'react';
import { useWrapRequest } from '@components/hooks/use-wrap-request';
import { useSearchReducer } from '@components/hooks/useSearchReducer';
import { Api } from '@core/api';

import { injectTSDI } from '@core/tsdi';

export function useStore(communityId: number) {
    const { challengeClient } = injectTSDI(Api);

    const [currencyCode, setCurrencyCode] = React.useState<string | undefined>(
        undefined
    );

    const [searchParams, dispatch] = React.useReducer(useSearchReducer, {
        size: 30,
        page: 0
    });

    const budgets = useWrapRequest(
        async () => {
            const result =
                await challengeClient.support.communityBudgetSupportControllerGetBudgets(
                    communityId
                );

            return { ...result.data, currencies: result.data.currencies || [] };
        },
        { defaultData: { communityId, currencies: [] } }
    );

    const usages = useWrapRequest(
        async (params?: QueryParamsDto, currency?: string) => {
            const result =
                await challengeClient.support.communityBudgetSupportControllerGetUsages(
                    {
                        communityId,
                        currencyCode: currency,
                        ...params
                    }
                );

            return { ...result.data, content: result.data.content || [] };
        },
        {
            defaultData: {
                content: [],
                number: 0,
                size: 0,
                totalPages: 0,
                totalElements: 0
            }
        }
    );

    const debouncedUsagesCall = React.useCallback(
        debounce((params?: QueryParamsDto, currency?: string) => {
            usages.request([params, currency]);
        }, 100),
        []
    );

    React.useEffect(() => {
        budgets.request([]);
    }, []);

    React.useEffect(() => {
        debouncedUsagesCall(searchParams, currencyCode);
    }, [searchParams, currencyCode, debouncedUsagesCall]);

    const reload = () => {
        budgets.request([]);
        dispatch({ type: 'reload' });
    };

    const correctUsage = async (
        usageId: number,
        value: number,
        comment: string
    ) => {
        await challengeClient.support.communityBudgetSupportControllerCorrectUsage(
            usageId,
            { value, comment }
        );
        reload();
    };

    const changeBudget = async (
        currency: string,
        budgetValue: number | undefined,
        budgetHardCap: number | undefined,
        comment: string
    ) => {
        await challengeClient.support.communityBudgetSupportControllerChangeBudget(
            communityId,
            currency,
            { budgetValue, budgetHardCap, comment }
        );
        reload();
    };

    const deleteBudget = async (currency: string, comment: string) => {
        await challengeClient.support.communityBudgetSupportControllerDeleteBudget(
            communityId,
            currency,
            { comment }
        );
        reload();
    };

    return {
        budgets,
        usages,
        searchParams,
        dispatch,
        currencyCode,
        setCurrencyCode,
        correctUsage,
        changeBudget,
        deleteBudget,
        reload
    };
}
